import { useEffect, useRef } from 'react';

/**
 * Run `callback` on `intervalMs` only while the document is visible.
 * When the tab becomes visible again, run immediately if at least one
 * interval has elapsed since the last run (or since polling started).
 * Cleans up the timer and the visibility listener on unmount / disable.
 */
export function useVisiblePolling(callback, intervalMs, enabled = true) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  const lastRunAtRef = useRef(0);
  const periodStartedAtRef = useRef(0);

  useEffect(() => {
    if (!enabled || !intervalMs || intervalMs <= 0) return undefined;
    if (typeof document === 'undefined') return undefined;

    periodStartedAtRef.current = Date.now();
    let intervalId = null;

    const run = () => {
      lastRunAtRef.current = Date.now();
      void callbackRef.current();
    };

    const startInterval = () => {
      if (intervalId != null) return;
      intervalId = window.setInterval(() => {
        if (document.visibilityState === 'visible') run();
      }, intervalMs);
    };

    const stopInterval = () => {
      if (intervalId == null) return;
      window.clearInterval(intervalId);
      intervalId = null;
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const anchor = lastRunAtRef.current || periodStartedAtRef.current;
        if (Date.now() - anchor >= intervalMs) run();
        startInterval();
      } else {
        stopInterval();
      }
    };

    if (document.visibilityState === 'visible') {
      startInterval();
    }

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      stopInterval();
    };
  }, [enabled, intervalMs]);
}
